/**
 * Interaction Tracking Service
 *
 * Comprehensive service for tracking all user interactions across the application.
 * Tracks clicks, navigation, form submissions, modal interactions, and more.
 *
 * Features:
 * - Automatic session management
 * - Silent failure (never disrupts user experience)
 * - Privacy-respecting (only tracks authenticated users)
 * - Flexible metadata support
 * - Performance optimized (async, non-blocking)
 *
 * Usage:
 * ```typescript
 * import { interactionTracking } from '@/services/interactionTrackingService';
 *
 * // Track a button click
 * interactionTracking.trackClick('start-learning-btn', 'button', { chapter: 'chapter-0' });
 *
 * // Track navigation
 * interactionTracking.trackNavigation('/dashboard', '/chapter-0');
 *
 * // Track form submission
 * interactionTracking.trackFormSubmit('contact-form', { name: 'John', email: 'john@example.com' });
 * ```
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * Supported interaction event types
 */
export type InteractionEventType =
  | 'click'           // Button/link clicks
  | 'navigation'      // Page navigation
  | 'form_submit'     // Form submissions
  | 'modal_open'      // Modal/dialog opened
  | 'modal_close'     // Modal/dialog closed
  | 'tab_change'      // Tab switching
  | 'scroll'          // Scroll events (use sparingly)
  | 'download';       // File downloads

/**
 * Supported UI element types
 */
export type InteractionElementType =
  | 'button'
  | 'link'
  | 'form'
  | 'card'
  | 'icon'
  | 'menu'
  | 'tab'
  | 'modal'
  | 'dropdown';

/**
 * Parameters for tracking an interaction
 */
export interface TrackInteractionParams {
  /** Type of interaction event */
  eventType: InteractionEventType;

  /** ID of the element that was interacted with */
  elementId?: string;

  /** Type of UI element */
  elementType?: InteractionElementType;

  /** Current page path */
  pagePath: string;

  /** Destination path (for navigation events) */
  destinationPath?: string;

  /** Chapter context (e.g., 'chapter-0', 'chapter-1') */
  chapter?: string;

  /** Lesson identifier (if within a lesson) */
  lessonId?: string;

  /** Component name that triggered the event */
  componentName?: string;

  /** Additional metadata (flexible key-value pairs) */
  metadata?: Record<string, any>;
}

/**
 * Interaction tracking service class
 * Singleton pattern for consistent session management
 */
class InteractionTrackingService {
  private sessionId: string;
  private isEnabled: boolean = true;
  private queue: TrackInteractionParams[] = [];
  private isProcessing: boolean = false;

  constructor() {
    this.sessionId = this.generateSessionId();
    console.log('[InteractionTracking] Service initialized with session:', this.sessionId);
  }

  /**
   * Generate a unique session identifier
   * Format: session_[timestamp]_[random]
   */
  private generateSessionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `session_${timestamp}_${random}`;
  }

  /**
   * Enable/disable interaction tracking
   * Useful for testing or privacy controls
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log(`[InteractionTracking] Tracking ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Track a user interaction
   *
   * @param params - Interaction parameters
   * @returns Promise<void> - Resolves when tracking is complete
   *
   * @example
   * ```typescript
   * await interactionTracking.trackInteraction({
   *   eventType: 'click',
   *   elementId: 'start-button',
   *   elementType: 'button',
   *   pagePath: '/dashboard',
   *   chapter: 'chapter-0',
   *   componentName: 'Dashboard',
   *   metadata: { buttonText: 'Start Learning' }
   * });
   * ```
   */
  async trackInteraction(params: TrackInteractionParams): Promise<void> {
    // Check if tracking is enabled
    if (!this.isEnabled) {
      return;
    }

    try {
      // Check authentication status (fail silently if not authenticated)
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        // Don't track anonymous users - fail silently
        console.debug('[InteractionTracking] Skipping tracking - no authenticated user');
        return;
      }

      // Prepare interaction data
      const interactionData = {
        user_id: user.id,
        event_type: params.eventType,
        element_id: params.elementId || null,
        element_type: params.elementType || null,
        page_path: params.pagePath,
        destination_path: params.destinationPath || null,
        chapter: params.chapter || null,
        lesson_content_id: params.lessonId || null, // Fixed: column name is lesson_content_id, not lesson_id
        component_name: params.componentName || null,
        metadata: params.metadata || {},
        session_id: this.sessionId,
        created_at: new Date().toISOString()
      };

      // Insert into database (non-blocking)
      const { error: insertError } = await supabase
        .from('user_analytics_events')
        .insert(interactionData);

      if (insertError) {
        // Log error but don't throw - fail silently
        console.warn('[InteractionTracking] Failed to track interaction:', {
          error: insertError.message,
          eventType: params.eventType,
          elementId: params.elementId
        });
      } else {
        console.debug('[InteractionTracking] Tracked:', {
          eventType: params.eventType,
          elementId: params.elementId,
          page: params.pagePath
        });
      }
    } catch (error) {
      // Catch all errors and fail silently
      console.error('[InteractionTracking] Unexpected error:', error);
      // Don't throw - never disrupt user experience
    }
  }

  /**
   * Track a button/link click
   *
   * @param elementId - ID of the clicked element
   * @param elementType - Type of element (button, link, etc.)
   * @param additionalData - Additional context data
   *
   * @example
   * ```typescript
   * interactionTracking.trackClick('start-btn', 'button', {
   *   chapter: 'chapter-0',
   *   componentName: 'Dashboard'
   * });
   * ```
   */
  trackClick(
    elementId: string,
    elementType: InteractionElementType = 'button',
    additionalData?: Partial<TrackInteractionParams>
  ): Promise<void> {
    return this.trackInteraction({
      eventType: 'click',
      elementId,
      elementType,
      pagePath: window.location.pathname,
      ...additionalData
    });
  }

  /**
   * Track page navigation
   *
   * @param from - Source page path
   * @param to - Destination page path
   * @param chapter - Optional chapter context
   * @param metadata - Optional additional data
   *
   * @example
   * ```typescript
   * interactionTracking.trackNavigation('/dashboard', '/chapter-0', 'chapter-0');
   * ```
   */
  trackNavigation(
    from: string,
    to: string,
    chapter?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    return this.trackInteraction({
      eventType: 'navigation',
      pagePath: from,
      destinationPath: to,
      elementType: 'link',
      chapter,
      metadata
    });
  }

  /**
   * Track form submission
   *
   * @param formId - ID of the submitted form
   * @param formData - Optional form data (sensitive data should be excluded)
   * @param additionalData - Additional context
   *
   * @example
   * ```typescript
   * interactionTracking.trackFormSubmit('contact-form', {
   *   formType: 'contact',
   *   fieldCount: 3
   * });
   * ```
   */
  trackFormSubmit(
    formId: string,
    formData?: Record<string, any>,
    additionalData?: Partial<TrackInteractionParams>
  ): Promise<void> {
    return this.trackInteraction({
      eventType: 'form_submit',
      elementId: formId,
      elementType: 'form',
      pagePath: window.location.pathname,
      metadata: formData,
      ...additionalData
    });
  }

  /**
   * Track modal open
   *
   * @param modalId - ID of the opened modal
   * @param modalType - Type/purpose of modal
   * @param additionalData - Additional context
   *
   * @example
   * ```typescript
   * interactionTracking.trackModalOpen('help-modal', 'help');
   * ```
   */
  trackModalOpen(
    modalId: string,
    modalType?: string,
    additionalData?: Partial<TrackInteractionParams>
  ): Promise<void> {
    return this.trackInteraction({
      eventType: 'modal_open',
      elementId: modalId,
      elementType: 'modal',
      pagePath: window.location.pathname,
      metadata: modalType ? { modalType } : undefined,
      ...additionalData
    });
  }

  /**
   * Track modal close
   *
   * @param modalId - ID of the closed modal
   * @param reason - Reason for closing (user action, completion, etc.)
   * @param additionalData - Additional context
   *
   * @example
   * ```typescript
   * interactionTracking.trackModalClose('help-modal', 'user_dismissed');
   * ```
   */
  trackModalClose(
    modalId: string,
    reason?: string,
    additionalData?: Partial<TrackInteractionParams>
  ): Promise<void> {
    return this.trackInteraction({
      eventType: 'modal_close',
      elementId: modalId,
      elementType: 'modal',
      pagePath: window.location.pathname,
      metadata: reason ? { reason } : undefined,
      ...additionalData
    });
  }

  /**
   * Track tab change
   *
   * @param tabId - ID of the tab that was selected
   * @param tabLabel - Label/name of the tab
   * @param additionalData - Additional context
   *
   * @example
   * ```typescript
   * interactionTracking.trackTabChange('tab-lessons', 'Lessons');
   * ```
   */
  trackTabChange(
    tabId: string,
    tabLabel?: string,
    additionalData?: Partial<TrackInteractionParams>
  ): Promise<void> {
    return this.trackInteraction({
      eventType: 'tab_change',
      elementId: tabId,
      elementType: 'tab',
      pagePath: window.location.pathname,
      metadata: tabLabel ? { tabLabel } : undefined,
      ...additionalData
    });
  }

  /**
   * Track file download
   *
   * @param fileId - ID of the file
   * @param fileName - Name of the downloaded file
   * @param fileType - Type/extension of file
   * @param additionalData - Additional context
   *
   * @example
   * ```typescript
   * interactionTracking.trackDownload('project-charter', 'charter.pdf', 'pdf');
   * ```
   */
  trackDownload(
    fileId: string,
    fileName: string,
    fileType?: string,
    additionalData?: Partial<TrackInteractionParams>
  ): Promise<void> {
    return this.trackInteraction({
      eventType: 'download',
      elementId: fileId,
      pagePath: window.location.pathname,
      metadata: {
        fileName,
        fileType: fileType || fileName.split('.').pop()
      },
      ...additionalData
    });
  }

  /**
   * Track card/tile click (common in dashboard/hub pages)
   *
   * @param cardId - ID of the clicked card
   * @param cardTitle - Title/name of the card
   * @param destination - Where the card navigates to
   * @param additionalData - Additional context
   *
   * @example
   * ```typescript
   * interactionTracking.trackCardClick('lesson-1-card', 'Introduction to AI', '/chapter-0/lesson-1');
   * ```
   */
  trackCardClick(
    cardId: string,
    cardTitle: string,
    destination?: string,
    additionalData?: Partial<TrackInteractionParams>
  ): Promise<void> {
    return this.trackInteraction({
      eventType: 'click',
      elementId: cardId,
      elementType: 'card',
      pagePath: window.location.pathname,
      destinationPath: destination,
      metadata: { cardTitle },
      ...additionalData
    });
  }

  /**
   * Flush any queued interactions (for cleanup/logout)
   * Not currently used but available for future batch processing
   */
  async flush(): Promise<void> {
    if (this.queue.length === 0) {
      return;
    }

    console.log(`[InteractionTracking] Flushing ${this.queue.length} queued interactions`);

    const itemsToProcess = [...this.queue];
    this.queue = [];

    for (const params of itemsToProcess) {
      await this.trackInteraction(params);
    }
  }

  /**
   * Reset session (useful for testing or new login)
   */
  resetSession(): void {
    this.sessionId = this.generateSessionId();
    console.log('[InteractionTracking] Session reset:', this.sessionId);
  }
}

// Export singleton instance
export const interactionTracking = new InteractionTrackingService();

// Export types for external use
export type {
  TrackInteractionParams,
  InteractionEventType,
  InteractionElementType
};
